import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, Checkbox, EmptyState, Field, Notice, PageHeader, StatusIndicator, Tag } from '@voice/ui';
import { errorText } from '../components/common';
import { api } from '../lib/api';
import { useSoftphone } from '../lib/softphone';
import type { Forwarding } from '../lib/types';
import { PhonebookInfo } from './Contacts';
import styles from './Pages.module.css';

interface Mine {
  extensions: { id: string; number: string; displayName: string; dnd: boolean; forwarding: Forwarding }[];
}
interface MyQueues {
  queues: { id: string; name: string; number: string | null; waiting: number; agents: { extensionId: string; state: string }[] }[];
}

export function MyPhone() {
  const query = useQuery({ queryKey: ['me-extension'], queryFn: () => api<Mine>('/me/extension') });
  const phone = useSoftphone();
  const [book, setBook] = useState(false);
  return (
    <div className={styles.page}>
      <PageHeader title="Mein Telefon">
        <Button variant="secondary" onClick={() => setBook(true)}>
          Kontakte aufs Tischtelefon
        </Button>
      </PageHeader>
      <section style={{ marginBottom: 32 }}>
        <h2 className={styles.heading}>Telefon im Browser</h2>
        <div className={styles.stack}>
          <StatusIndicator state={phone.status === 'ready' ? 'ok' : phone.status === 'error' ? 'busy' : 'off'} label={phone.status === 'ready' ? `Bereit als Nebenstelle ${phone.extension?.number}` : phone.status === 'error' ? phone.error : phone.status === 'connecting' ? 'Verbindet …' : 'Aus'} />
          <Checkbox label="Anrufe im Browser klingeln lassen" hint="Der Browser fragt beim ersten Gespräch nach dem Mikrofon. Der Tab muss geöffnet bleiben." checked={phone.enabled} onChange={(e) => phone.setEnabled(e.target.checked)} />
        </div>
      </section>
      {query.data?.extensions.length === 0 && <EmptyState>Dir ist noch keine Nebenstelle zugeordnet. Das richtet ein Administrator ein.</EmptyState>}
      {query.data?.extensions.map((e) => <ExtensionSettings key={e.id} ext={e} />)}
      <MyQueuesSection />
      {book && <PhonebookInfo onClose={() => setBook(false)} />}
    </div>
  );
}

function ExtensionSettings({ ext }: { ext: Mine['extensions'][number] }) {
  const qc = useQueryClient();
  const [dnd, setDnd] = useState(ext.dnd);
  const [fwd, setFwd] = useState<Forwarding>(ext.forwarding);
  const save = useMutation({
    mutationFn: () => api(`/me/extension/${ext.id}`, { method: 'PATCH', body: { dnd, forwarding: { always: fwd.always ?? '', busy: fwd.busy ?? '', noAnswer: fwd.noAnswer ?? '', ...(fwd.noAnswerSeconds ? { noAnswerSeconds: fwd.noAnswerSeconds } : {}) } } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me-extension'] }),
  });
  const f = (key: 'always' | 'busy' | 'noAnswer', label: string) => <Field label={label} inputMode="numeric" value={fwd[key] ?? ''} onChange={(e) => setFwd({ ...fwd, [key]: e.target.value.replace(/\D/g, '') })} placeholder="Nebenstelle oder Nummer" />;
  return (
    <section style={{ marginBottom: 32 }}>
      <h2 className={styles.heading}>
        Nebenstelle {ext.number} <span className={styles.muted}>{ext.displayName}</span>
      </h2>
      <div className={styles.form}>
        <Checkbox label="Nicht stören" hint="Anrufer hören besetzt bzw. werden umgeleitet." checked={dnd} onChange={(e) => setDnd(e.target.checked)} />
        <div className={styles.row}>
          {f('always', 'Immer umleiten auf')}
          {f('busy', 'Bei besetzt umleiten auf')}
          {f('noAnswer', 'Bei Nichtmelden umleiten auf')}
        </div>
        {save.isError && <Notice tone="error">{errorText(save.error)}</Notice>}
        {save.isSuccess && <Notice tone="success">Gespeichert.</Notice>}
        <div>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            Speichern
          </Button>
        </div>
      </div>
    </section>
  );
}

function MyQueuesSection() {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['me-queues'], queryFn: () => api<MyQueues>('/me/queues') });
  const set = useMutation({ mutationFn: (v: { id: string; loggedIn?: boolean; paused?: boolean }) => api(`/me/queues/${v.id}`, { method: 'POST', body: { loggedIn: v.loggedIn, paused: v.paused } }), onSuccess: () => qc.invalidateQueries({ queryKey: ['me-queues'] }) });
  if (!query.data || query.data.queues.length === 0) return null;
  return (
    <section>
      <h2 className={styles.heading}>Meine Warteschlangen</h2>
      <div className={styles.deviceList}>
        {query.data.queues.map((q) => {
          const state = q.agents[0]?.state ?? 'loggedout';
          return (
            <div key={q.id} className={styles.device}>
              <div className={styles.deviceInfo}>
                <span className={styles.deviceName}>
                  {q.name} {q.waiting > 0 && <Tag tone="warn">{q.waiting} wartend</Tag>}
                </span>
                <span className={styles.muted}>{state === 'loggedout' ? 'Abgemeldet' : state === 'paused' ? 'In Pause' : 'Angemeldet'}</span>
              </div>
              <div className={styles.inline}>
                <Button variant="secondary" onClick={() => set.mutate({ id: q.id, loggedIn: state === 'loggedout' })}>
                  {state === 'loggedout' ? 'Anmelden' : 'Abmelden'}
                </Button>
                {state !== 'loggedout' && (
                  <Button variant="secondary" onClick={() => set.mutate({ id: q.id, paused: state !== 'paused' })}>
                    {state === 'paused' ? 'Pause beenden' : 'Pause'}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
